import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

export const formatInvoiceEmailDate = (raw) => {
  const date = new Date(raw);
  if (!Number.isFinite(date.getTime())) return 'Unknown date';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(date);
};

export const invoiceEmailTimeAgo = (sentAt, now = Date.now()) => {
  const date = new Date(sentAt).getTime();
  if (!Number.isFinite(date)) return 'Unknown';
  const elapsed = Math.max(0, Number(now) - date);
  const minutes = Math.floor(elapsed / 60000);
  const hours = Math.floor(elapsed / 3600000);
  const days = Math.floor(elapsed / 86400000);
  if (minutes < 1) return 'Just now';
  if (hours < 1) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export default function SalesOrderInvoiceEmailHistory({
  events = [], loading = false, error = '', showWhenEmpty = false, onRefresh,
  onOpenInvoice, now = Date.now(), defaultExpanded = false,
}) {
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const rows = Array.isArray(events) ? events : [];
  const visible = showAll ? rows : rows.slice(0, 3);

  if (!showWhenEmpty && !rows.length && !error && !loading) return null;

  return (
    <Box data-testid="so-invoice-email-history" sx={{
      mb: 1.25, px: { xs: 1.25, sm: 1.5 }, py: 1,
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        gap={1} flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={0.85}>
          <MarkEmailReadOutlinedIcon color="action" sx={{ fontSize: 19 }} />
          <Typography variant="body2" sx={{ fontWeight: 750 }}>
            Email history
          </Typography>
          {rows.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {rows.length} send{rows.length === 1 ? '' : 's'} · last {invoiceEmailTimeAgo(rows[0].sentAt, now)}
            </Typography>
          ) : null}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button size="small" sx={{ textTransform: 'none' }}
            onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
            {expanded ? 'Hide details' : 'View details'}
          </Button>
          {expanded && rows.length > 3 ? (
            <Button size="small" sx={{ textTransform: 'none' }}
              onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show recent' : `View all ${rows.length}`}
            </Button>
          ) : null}
          {expanded && onRefresh ? (
            <Button size="small" sx={{ textTransform: 'none' }} onClick={onRefresh}
              disabled={loading}>
              Refresh
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {expanded && loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={15} />
          <Typography variant="caption" color="text.secondary">Checking recorded sends…</Typography>
        </Stack>
      ) : null}
      {error ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Invoice email history could not be loaded. It does not mean no email was sent.
        </Alert>
      ) : null}
      {expanded && !loading && !error && rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          No invoice email sends have been recorded yet. Older emails may predate this history.
        </Typography>
      ) : null}
      {expanded && !loading && rows.length > 0 ? (
        <>
          <TableContainer sx={{ mt: 0.7, maxHeight: showAll ? 260 : 'none' }}>
            <Table size="small" stickyHeader={showAll} aria-label="Invoice email send history"
              sx={{ '& .MuiTableCell-root': { px: 0.8, py: 0.7, fontSize: 12.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Sent to</TableCell>
                  <TableCell>Sent on</TableCell>
                  <TableCell>How long ago</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((row) => (
                  <TableRow key={row.id || `${row.invoiceId}-${row.sentAt}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {onOpenInvoice ? (
                        <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 750 }}
                          endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 13 }} />}
                          onClick={() => onOpenInvoice(row.invoiceId)}>
                          #{row.invoiceId}
                        </Button>
                      ) : <Typography variant="body2">#{row.invoiceId}</Typography>}
                      {row.source === 'resend' ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Resent{row.isVoided ? ' · Voided' : ''}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ minWidth: 125, maxWidth: 190 }}>
                      <Tooltip title={[
                        row.subject ? `Subject: ${row.subject}` : '',
                        row.sentByName ? `Sent by: ${row.sentByName}` : '',
                        row.cc?.length ? `CC: ${row.cc.join(', ')}` : '',
                      ].filter(Boolean).join(' · ')} arrow>
                        <Box>
                          <Typography variant="body2" sx={{
                            fontSize: 12.5, textOverflow: 'ellipsis', overflow: 'hidden',
                            whiteSpace: 'nowrap', maxWidth: 190,
                          }} title={(row.to || []).join(', ')}>
                            {(row.to || []).join(', ') || 'Recipient unavailable'}
                          </Typography>
                          {row.sentByName ? (
                            <Typography variant="caption" color="text.secondary">
                              By {row.sentByName}
                            </Typography>
                          ) : null}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatInvoiceEmailDate(row.sentAt)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12.5 }}>
                        {invoiceEmailTimeAgo(row.sentAt, now)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Dates shown in your local time. “Sent” means accepted by the email provider, not confirmed delivered or opened.
          </Typography>
        </>
      ) : null}
    </Box>
  );
}
